<template>
  <Container class="flex-col align-start ast-page">
    <h2 class="ast-title">客户售后跟踪信息管理</h2>
    <div class="ast-toolbar flex-wrap">
      <Input
        search
        clearable
        placeholder="项目 / 合同关键字"
        class="ast-toolbar-input"
      />
      <Select placeholder="履约状态" transfer class="ast-toolbar-select" clearable />
      <Button type="primary" icon="ios-search">查询</Button>
      <Button icon="md-download">导出</Button>
    </div>
    <div class="ast-table-wrap">
      <BaseTable
        ref="table"
        :columns="cols"
        :data="tableRows"
        :option="tableOption"
        full
        border
      />
    </div>
  </Container>
</template>

<script>
import Container from '@/components/app/container';
import visitTrackingStatic from './after-sales-tracking.data.js';

/** 从首段【】取统计年度 */
function extractVisitYear(text) {
  const m = String(text || '').match(/【([^】]+)】/);
  if (!m) {
    return '—';
  }
  const inner = m[1];
  const tail = inner.match(/·(\d{4})$/);
  if (tail) {
    return tail[1];
  }
  const head = inner.match(/^(\d{4})/);
  if (head) {
    return head[1];
  }
  const any = inner.match(/(\d{4})/);
  return any ? any[1] : '—';
}

function stripSource(text) {
  let s = String(text || '').trim();
  const i = s.indexOf('来源：');
  if (i >= 0) {
    s = s.slice(0, i).trim();
  }
  return s;
}

/** 压缩「上半年/下半年」一句，去掉冗词 */
function compressVisitHalf(segment) {
  if (!segment) {
    return '—';
  }
  let s = segment.trim().replace(/。$/u, '');
  if (/^全年/.test(s)) {
    return '';
  }
  const isHalf = s.startsWith('上半年') || s.startsWith('下半年');
  if (!isHalf) {
    return s.length > 22 ? `${s.slice(0, 22)}…` : s;
  }
  s = s
    .replace(/^上半年/, '')
    .replace(/^下半年/, '')
    .replace(/^[：:\s]+/, '')
    .trim();
  if (!s) {
    return '—';
  }
  if (/无访问|未开工/.test(s)) {
    return '无';
  }
  let out = s
    .replace(/较满意(\d+)次/g, '较满意×$1')
    .replace(/满意(\d+)次/g, '满意×$1')
    .replace(/认可(\d+)次/g, '认可×$1')
    .replace(/不满意(\d+)次/g, '不满意×$1');
  out = out.replace(/、/g, ' ');
  if (out.length > 18) {
    out = `${out.slice(0, 18)}…`;
  }
  return out || '—';
}

/**
 * 从 subject 拆出：年份、上半年摘要、下半年摘要（不含「来源」与「全年合计」句）
 */
function parseVisitColumns(text) {
  const year = extractVisitYear(text);
  let t = stripSource(text);
  let body = t.replace(/【[^】]+】\s*/, '').trim();
  const vi = body.indexOf('顾客访问：');
  if (vi >= 0) {
    body = body
      .slice(vi + '顾客访问：'.length)
      .replace(/^[：:\s]+/, '')
      .trim();
  }
  const parts = body.split('；').map((p) => p.trim()).filter(Boolean);
  let h1 = '—';
  let h2 = '—';
  for (const p of parts) {
    if (p.startsWith('全年')) {
      continue;
    }
    if (p.startsWith('上半年')) {
      h1 = compressVisitHalf(p) || '—';
    } else if (p.startsWith('下半年')) {
      h2 = compressVisitHalf(p) || '—';
    }
  }
  if (h1 === '—' && h2 === '—' && body) {
    const m = body.match(/共(\d+)项/);
    if (m) {
      h1 = `${m[1]}项·满意`;
      h2 = '—';
    } else {
      const one = body.replace(/。$/u, '');
      h1 = one.length > 26 ? `${one.slice(0, 26)}…` : one;
    }
  }
  return {
    year,
    firstHalf: h1,
    secondHalf: h2,
  };
}

function splitContract(name) {
  const s = String(name || '');
  const i = s.lastIndexOf('（');
  if (i <= 0 || !s.endsWith('）')) {
    return {base: s, badge: ''};
  }
  return {base: s.slice(0, i).trim(), badge: s.slice(i + 1, -1).trim()};
}

/** 上半年/下半年格：无→灰、满意(满×/较×/含满意)→绿、不满意(否×)→红 */
function visitHalfCellClass(text) {
  const s = String(text || '').trim();
  if (!s || s === '—') {
    return ['ast-visit-td', 'ast-visit-na'];
  }
  if (/否|不满意/.test(s)) {
    return ['ast-visit-td', 'ast-visit-bad'];
  }
  if (s === '无') {
    return ['ast-visit-td', 'ast-visit-muted'];
  }
  if ((/满|较/.test(s) || /满意/.test(s)) && !/不满意/.test(s)) {
    return ['ast-visit-td', 'ast-visit-good'];
  }
  return ['ast-visit-td'];
}

function renderVisitTd(h, text) {
  const s = text || '—';
  return h('span', {class: visitHalfCellClass(s), attrs: {title: s}}, s);
}

export default {
  routerOption: {},
  components: {Container},
  data() {
    return {
      tableOption: {
        sort_key: 'updated_at',
        sort_order: 'desc',
        stripe: true,
      },
    };
  },
  computed: {
    tableRows() {
      const base = visitTrackingStatic.map((row, i) => {
        const v = parseVisitColumns(row.subject);
        return {
          ...row,
          id: i + 1,
          visit_year: v.year,
          visit_h1: v.firstHalf,
          visit_h2: v.secondHalf,
        };
      });
      return [...base].sort((a, b) =>
        String(b.updated_at || '').localeCompare(String(a.updated_at || '')),
      );
    },
    cols() {
      const vm = this;
      const core = [
        {
          type: 'index',
          title: '序号',
          width: 56,
        },
        {
          title: '合同 / 项目',
          key: 'contract',
          minWidth: 188,
          maxWidth:400,
          align: 'center',
          sortable: true,
          render(h, {row}) {
            const {base, badge} = splitContract(row.contract);
            const nodes = [
              h('span', {class: 'ast-contract-name', attrs: {title: row.contract}}, base || '—'),
            ];
            if (badge) {
              nodes.push(
                h(
                  'Tag',
                  {
                    props: {
                      color: vm.contractBadgeColor(badge),
                    },
                    class: 'ast-contract-tag',
                  },
                  badge,
                ),
              );
            }
            return h('div', {class: 'ast-contract-cell flex-wrap', style: 'padding:0 10px'}, nodes);
          },
        },
        {
          title: '年份',
          key: 'visit_year',
          width: 72,
          align: 'center',
          sortable: true,
        },
        {
          title: '上半年',
          key: 'visit_h1',
          width: 120,
          align: 'center',
          sortable: false,
          render(h, {row}) {
            return renderVisitTd(h, row.visit_h1);
          },
        },
        {
          title: '下半年',
          key: 'visit_h2',
          width: 120,
          align: 'center',
          sortable: false,
          render(h, {row}) {
            return renderVisitTd(h, row.visit_h2);
          },
        },
      ];
      const tail = [
        {
          title: '顾客满意',
          key: 'status',
          width: 100,
          align: 'center',
          sortable: true,
          render(h, {row}) {
            return h(
              'Tag',
              {props: {color: vm.satisfactionColor(row.status)}},
              row.status || '—',
            );
          },
        },
        {
          title: '最近更新',
          key: 'updated_at',
          width: 118,
          type: 'time',
          sortable: true,
        },
        {
          title: '责任人',
          key: 'owner',
          width: 96,
          align: 'center',
          sortable: true,
        },
      ];
      return [...core, ...tail];
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.table && this.$refs.table.calcTableHeight) {
        setTimeout(() => this.$refs.table.calcTableHeight(), 300);
      }
    });
  },
  methods: {
    contractBadgeColor(badge) {
      if (/暂停|待接购|未开工/.test(badge)) {
        return 'warning';
      }
      if (/竣工|完工/.test(badge)) {
        return 'success';
      }
      return 'default';
    },
    satisfactionColor(status) {
      const s = status || '';
      if (s.includes('不满意')) {
        return 'error';
      }
      if (s.includes('需关注')) {
        return 'warning';
      }
      if (s.includes('较满意')) {
        return 'blue';
      }
      if (s.includes('满意')) {
        return 'success';
      }
      if (s.includes('未访问')) {
        return 'default';
      }
      return 'default';
    },
  },
};
</script>

<style lang="less" scoped>
.ast-page {
  background: #f5f7fa;
  min-height: 100%;
}

.ast-title {
  padding: 8px 0 10px;
  margin: 6px 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a2e;
  letter-spacing: 0.02em;
  border-bottom: 2px solid var(--primary, #2d8cf0);
}

.ast-toolbar {
  margin: 14px 20px 12px;
  padding: 12px 14px;
  gap: 10px;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.ast-toolbar-input {
  width: 240px;
}

.ast-toolbar-select {
  width: 140px;
}

.ast-table-wrap {
  padding: 0 20px 16px;
  height: calc(100% - 132px);
  width: 100%;
  position: relative;
}

.ast-contract-cell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  text-align: left;
}

.ast-contract-name {
  color: #2c3e50;
  font-weight: 500;
  line-height: 1.4;
}

.ast-contract-tag {
  flex-shrink: 0;
}

.ast-visit-td {
  display: inline-block;
  max-width: 100%;
  padding: 4px 6px;
  font-size: 13px;
  color: #4a5568;
  line-height: 1.35;
  vertical-align: middle;
}

.ast-visit-na {
  color: #a0aec0;
}

.ast-visit-muted {
  color: #8c8c8c;
}

.ast-visit-good {
  color: #389e0d;
  font-weight: 500;
}

.ast-visit-bad {
  color: #cf1322;
  font-weight: 500;
}
</style>
